import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, AfterViewInit, inject } from '@angular/core';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { _AbcGridColumn, IStorage, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { WorkGroupsConfigMode, WorkState } from '../create-works-wizard/models/WorkWizardOptions';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { WorkService } from '../create-works-wizard/services/work.service';
import {
  MaintenanceCycleEvaluationResult,
  ObjectGroupGenerationResult,
  ObjectGroupService,
  ProcessGroupOptions,
} from './services/objectgroup.service';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
  ProtocolViewerService,
} from '@barthauer/abc/messages';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  mapTo,
  merge,
  Observable,
  of,
  publishReplay,
  refCount,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  firstValueFrom,
  from,
} from 'rxjs';
import { GroupSelectionComponent, ObjectSelectionGroup } from '@barthauer/abc/ui-frameworks/object-selection';
import { ObjectTypeInfoViewModel, QueryColumn, QueryColumnSortOrder, QueryParameter } from '@barthauer/abc/core/data';
import { dxDataGridColumn } from 'devextreme/ui/data_grid';
import { DataQueryOPathes } from '../create-works-wizard/models/data-query-opathes';
import { MtWorkGroupQueryInfo } from '../create-works-wizard/models/MtWorkGenerationInfo';
import { AppService } from '@barthauer/abc/maintenance/opera/core';
import { UserService } from '@barthauer/abc/core/authentication';

interface WorkGroup {
  Id: string;
  Description: string;
  CycleLength: number;
  CycleUnit: number;
  WorkTypeId?: string;
  CycleStartDate: Date | null;
  LeadTime: number;
}

@Component({
  selector: 'basys-opera-config-objectgroup-wizard',
  templateUrl: './config-objectgroup-wizard.component.html',
  styleUrls: ['./config-objectgroup-wizard.component.scss'],
  standalone: false,
})
export class ConfigObjectgroupWizardComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() closeWizardDialog = new EventEmitter();

  @ViewChild('stepper', { read: MatStepper, static: false }) stepper!: MatStepper;
  @ViewChild('stepWelcome', { read: MatStep, static: false }) stepWelcome!: MatStep;
  @ViewChild('stepSourceData', { read: MatStep, static: false }) stepSourceData!: MatStep;
  @ViewChild('stepObjectSelection', { read: MatStep, static: false }) stepObjectSelection!: MatStep;
  @ViewChild('groupSelectionComponent', { read: GroupSelectionComponent, static: false })
  groupSelectionComponent!: GroupSelectionComponent;
  @ViewChild('worktemplateSelectionComponent', { read: GroupSelectionComponent, static: false })
  worktemplateSelectionComponent!: GroupSelectionComponent;

  public objectSelectionGroups$!: Observable<ObjectSelectionGroup[]>;
  public isLoadingGroups$!: Observable<boolean>;
  public gridLayoutStorage: IStorage;
  public preselectedKeys: any[] = [];
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  public selectedMode: WorkGroupsConfigMode = WorkGroupsConfigMode.evaluate;
  public isLinear: boolean = true;
  public isEditable: boolean = true;
  public alive: boolean = true;
  public welcomeStepLabel: string;
  public isMaintenanceCycleChecked: boolean = false;
  public designation: string = '';
  public cycleLength: number = 1;
  public leadTime: number;
  public assetsCount: number = 0;
  public selectedCycleUnitId: number | null = null;
  public selectedGroupTypeId: string | null = null;
  public groupTemplateData: any;
  public workTemplateSelectionGroups$!: Observable<ObjectSelectionGroup[]>;
  public isLoadingWorkTemplateGroups$!: Observable<boolean>;
  public statusData: any;
  public firstExecutionDate: Date | null;
  public cycleUnitList: { Id: number; Description: string }[] = [];
  public workGroupData: WorkGroup[] = [];
  public selectedExistingGroup?: WorkGroup;
  public selectedAnlageGroupId: string | null = null;
  public alreadyAssignedIds: string[] = [];
  public originalAnlageDetailMap = new Map<string, string>();
  public dataCheckTitle: string;
  public buttonText: string;
  public buttonStatus: boolean = false;
  public isExistingWorkGroupMaintenanceCycleChecked: boolean = false;
  public existingWorkGroupCycleLength: number;
  public existingWorkGroupLeadTime: number;
  public existingWorkGroupCycleUnitId: number;
  public lastWorkGroupQueryInfo: MtWorkGroupQueryInfo;
  public displayBusybar = false;
  public leadTimeFormat: string = '#########0.0 Tag(e)';
  public arbeitStatusText: string;
  public arbeitStatusValidationMessage: string;

  private arbeitStatusNumber: number;
  private groupId: string;
  private unsubscribe$ = new Subject<void>();
  private objectTypeDesignations: Map<number, string> = new Map<number, string>();
  private readonly objSelectionGroupName = 'WorkGroupGenerationInfos';
  private readonly objSelectionGroupNameWithWorkTempalteColumn = 'WorkGroupGenerationInfosWithWorkTemplate';
  private readonly workTemplateSelectionGroupName = 'WorkTemplateGroupInfos';
  private readonly refreshGroupsToken$ = new BehaviorSubject(false);
  private readonly refreshWorkTemplateGroupsToken$ = new BehaviorSubject(false);
  private readonly lookupNewObjectTypeTrigger$ = new BehaviorSubject(0);
  private cycleWorkTypesTotalCount: number;
  private previousCycleWorkTypeIds: string[] = [];
  private cycleWorkTypeIds: string[] = [];

  constructor(
    private abcCore: AbcCore,
    private resourceService: ResourceManagerService,
    private workService: WorkService,
    private objectGroupService: ObjectGroupService,
    private protocolViewerService: ProtocolViewerService,
    private messageBoxService: MessageBoxService,
    private userService: UserService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('opera-wizards');
    this.workService.raiseErrorInfo
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((msg) => this.showMessageAsHtml(msg, MessageMode.Error));
  }

  async ngOnInit() {
    this.res = await this.resAsync;
    this.welcomeStepLabel = this.res.get('WizardWelcome');
    this.arbeitStatusValidationMessage = this.res.get('WorkStatusValidationText');
    this.firstExecutionDate = new Date();
    this.cycleWorkTypeIds = [];

    this.dataCheckTitle = this.res.get('CheckDataDescription');
    this.buttonText = this.res.get('ButtonTextStartExecution');

    this.workService.createWorkTypeAsDropDownListQuery(true).subscribe((result: any) => {
      this.groupTemplateData = result;
      this.cycleWorkTypesTotalCount = result.length;
    });

    this.workService
      .queryPicklistItems('state')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.statusData = result.Data;
      });

    this.workService.queryPicklistItems('CycleUnit').subscribe((result) => {
      this.cycleUnitList = result.Data.map((item: any) => ({
        Id: Number(item.Value),
        Description: item.Description,
      }));

      setTimeout(() => {
        const defaultItem = this.cycleUnitList.find((x) => x.Description === 'Woche(n)');
        this.selectedCycleUnitId = defaultItem?.Id ?? this.cycleUnitList[0]?.Id ?? null;
      });
    });

    this.objectSelectionGroups$ = this.refreshGroupsToken$.pipe(
      switchMap((allowRefresh) =>
        (allowRefresh ? this.createObjectSelectionGroups() : of([])).pipe(
          tap(
            () =>
              (this.lastWorkGroupQueryInfo = {
                Mode: this.selectedMode,
              }),
          ),
          catchError((err) => {
            this.abcCore.error.publish(err);
            return of([]);
          }),
        ),
      ),
    );

    this.workTemplateSelectionGroups$ = this.refreshWorkTemplateGroupsToken$.pipe(
      switchMap((allowRefresh) =>
        (allowRefresh ? from(this.createWorkTemplateSelectionGroups()) : of([])).pipe(
          catchError((err) => {
            this.abcCore.error.publish(err);
            return of([]);
          }),
        ),
      ),
    );

    this.isLoadingGroups$ = merge(
      this.refreshGroupsToken$.pipe(mapTo(true)),
      this.objectSelectionGroups$.pipe(
        catchError(() => of(undefined)),
        mapTo(false),
      ),
    ).pipe(publishReplay(1), refCount());

    this.isLoadingWorkTemplateGroups$ = merge(
      this.refreshWorkTemplateGroupsToken$.pipe(mapTo(true)),
      this.workTemplateSelectionGroups$.pipe(
        catchError(() => of(undefined)),
        mapTo(false),
      ),
    ).pipe(publishReplay(1), refCount());

    this.refreshObjectSelection();
    this.refreshWorkTemplateSelection();

    this.objectSelectionGroups$.subscribe((data) => {
      if (data.length > 0) {
      }
    });

    this.isLoadingGroups$.subscribe((loading) => {});

    this.lookupNewObjectTypeTrigger$
      .pipe(
        concatMap((newObjectTypeNumber) =>
          newObjectTypeNumber > 0
            ? this.abcCore.objectSpace.getObjectType(newObjectTypeNumber)
            : of(null as unknown as ObjectTypeInfoViewModel),
        ),
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: (otViewModel) => {
          if (otViewModel) {
            this.objectTypeDesignations.set(otViewModel.value, otViewModel.displayText);
          }
        },
      });

    await this.refreshWorkGroupData();

    this.objectGroupService.generationFinished
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: ObjectGroupGenerationResult) => {
        this.dataCheckTitle = this.res.get('OperationFinishedSuccessfully');
        this.buttonStatus = false;
        this.displayBusybar = false;

        this.generateProtocol(result);
      });

    this.objectGroupService.generationFailed.pipe(takeUntil(this.unsubscribe$)).subscribe((error) => {
      this.dataCheckTitle = this.res.get('OperationFinishedWithErrors');
      this.buttonStatus = false;
      this.displayBusybar = false;

      console.error('Failed during group processing:', error);
      this.workService.showErrorInfo('An unexpected error occurred while processing the group.');
    });

    this.objectGroupService.evaluationFinished
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: MaintenanceCycleEvaluationResult) => {
        this.dataCheckTitle = this.res.get('OperationFinishedSuccessfully');
        this.buttonStatus = false;
        this.displayBusybar = false;

        this.generateEvaluationProtocol(result);
      });

    this.objectGroupService.evaluationFailed.pipe(takeUntil(this.unsubscribe$)).subscribe((error) => {
      this.dataCheckTitle = this.res.get('OperationFinishedWithErrors');
      this.buttonStatus = false;
      this.displayBusybar = false;

      console.error('Failed during maintenance cycles evaluation:', error);
      this.workService.showErrorInfo('An unexpected error occurred while evaluating maintenance cycles.');
    });
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.alive = false;
    this.lookupNewObjectTypeTrigger$.complete();
    this.refreshGroupsToken$.complete();
    this.refreshWorkTemplateGroupsToken$.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public openHelp(): void {
    this.abcCore.openWindow('http://help.barthauer.de/BaSYS/9.26.1/Web/opera-manager');
  }

  public validateStep(step: MatStep): void {
    if (step === this.stepSourceData) {
      if (this.selectedMode === WorkGroupsConfigMode.createNew) {
        this.onStepValidation(step, this.validateSourceDataStep());
      } else if (this.selectedMode === WorkGroupsConfigMode.editExisting) {
        if (!this.selectedAnlageGroupId) {
          this.workService.showErrorInfo(`<div>${this.res.get('WorkGroupSelectionValidationText')}</div>`);
          return;
        }

        this.onStepValidation(step, this.validateEditExistingSourceDataStep());
      } else {
        this.onStepValidation(step, this.validateWorkTemplateAndStatusSelection());
      }
      if (this.selectedMode !== WorkGroupsConfigMode.evaluate) {
        this.cycleWorkTypeIds = [];
      }
    }
  }

  public moveBack(): void {
    this.stepper.previous();
    this.dataCheckTitle = this.res.get('CheckDataDescription');
    this.buttonText = this.res.get('ButtonTextStartExecution');
  }

  public onDesignationChanged(e: any): void {
    this.designation = e.value;
  }

  public onMaintenanceCycleChanged(e: any): void {
    this.isMaintenanceCycleChecked = e.value;
  }

  public onCycleLengthChanged(e: any): void {
    this.cycleLength = e.value;
  }

  public onCycleUnitChanged(e: any): void {
    this.selectedCycleUnitId = e.value;
  }

  public onLeadTimeChanged(e: any): void {
    this.leadTime = e.value;
  }

  private getWorkTypeLeadTime(workTypeId: string | null): number | null {
    if (!workTypeId) return null;
    const workType = this.groupTemplateData?.find((wt: any) => wt.Id === workTypeId);
    return workType?.leadTime || null;
  }

  public onGroupTemplateChanged(e: any): void {
    this.selectedGroupTypeId = e.value;

    this.leadTime = this.getWorkTypeLeadTime(this.selectedGroupTypeId);
    this.refreshObjectSelection();
  }

  public onFirstExecDateChanged(eventArgs: any): void {
    if (this.selectedMode === WorkGroupsConfigMode.createNew) {
      this.firstExecutionDate = new Date(ConvertHelper.toString(eventArgs));
    } else if (this.selectedMode === WorkGroupsConfigMode.editExisting) {
      this.firstExecutionDate = eventArgs;
    }
  }

  public async onWorkGroupChanged(e: any): Promise<void> {
    this.groupId = e.value;
    this.selectedExistingGroup = this.workGroupData.find((g) => g.Id === this.groupId);
    this.isExistingWorkGroupMaintenanceCycleChecked = false;
    if (this.selectedExistingGroup) {
      this.existingWorkGroupCycleLength = this.selectedExistingGroup.CycleLength;
      this.existingWorkGroupCycleUnitId = this.selectedExistingGroup.CycleUnit;
      this.firstExecutionDate = this.selectedExistingGroup.CycleStartDate;
      this.existingWorkGroupLeadTime = this.selectedExistingGroup.LeadTime;
    }

    const rawUnit = this.selectedExistingGroup?.CycleUnit;
    this.selectedCycleUnitId = rawUnit != null ? Number(rawUnit) : null;

    if (this.groupId) {
      try {
        const assignedDetails = await this.objectGroupService.getAssignedAnlageDetails(this.groupId);

        if (assignedDetails && assignedDetails.length > 0) {
          this.preselectedKeys = assignedDetails.map((detail) => detail.ObjectId);
          this.alreadyAssignedIds = [...this.preselectedKeys];
          this.originalAnlageDetailMap = new Map(assignedDetails.map((d) => [d.ObjectId, d.Id]));
        } else {
          this.preselectedKeys = [];
          this.alreadyAssignedIds = [];
          this.originalAnlageDetailMap.clear();
        }
      } catch (error) {
        console.error('Error loading assigned Anlagen:', error);
        this.preselectedKeys = [];
        this.alreadyAssignedIds = [];
        this.originalAnlageDetailMap.clear();
        this.workService.showErrorInfo('Die zugewiesenen Anlagen konnten nicht geladen werden.');
      }
    }
    this.refreshObjectSelection();
  }

  public onStatusChanged(eventArgs: any): void {
    this.arbeitStatusNumber = Number(ConvertHelper.toString(eventArgs.value)) as WorkState;
    this.arbeitStatusText = WorkState[this.arbeitStatusNumber];
  }

  private async refreshWorkGroupData(): Promise<void> {
    const result: any = await firstValueFrom(this.objectGroupService.createObjectGroupAsDropDownListQuery());

    const parsed = result?.Data ?? result;
    this.workGroupData = (Array.isArray(parsed) ? parsed : []).map((row: any) => ({
      Id: row.Id,
      Description: row.Designation,
      CycleLength: row.CycleLength,
      CycleUnit: row.CycleUnit,
      CycleUnitName: this.cycleUnitList.find((cu) => cu.Id === row.CycleUnit)?.Description ?? '',
      WorkTypeId: row.WorkTypeId,
      CycleStartDate: row.CycleStartDate,
      LeadTime: row.LeadTime,
    }));
  }

  public onExistingWorkGroupMaintenanceCycleChanged(e: any): void {
    this.isExistingWorkGroupMaintenanceCycleChecked = e.value;
  }

  public onExistingWorkGroupCycleLengthChanged(e: any): void {
    this.existingWorkGroupCycleLength = e.value;
  }

  public onExistingWorkGroupCycleUnitChanged(e: any): void {
    this.existingWorkGroupCycleUnitId = e.value;
  }

  public onExistingWorkGroupLeadTimeChanged(e: any): void {
    this.existingWorkGroupLeadTime = e.value;
  }

  public executeObjectGroupAction(): void {
    if (this.buttonText === this.res.get('ButtonTextClose')) {
      this.closeWizardDialog.emit();
    } else {
      this.displayBusybar = true;
      this.dataCheckTitle = this.res.get('OperationIsExecuted');

      this.buttonText = this.res.get('ButtonTextClose');
      this.buttonStatus = !this.buttonStatus;
      this.isEditable = false;

      if (this.selectedMode == WorkGroupsConfigMode.editExisting) {
        this.objectGroupService.updateObjectGroupCycle(
          this.groupId,
          this.existingWorkGroupCycleLength,
          this.existingWorkGroupCycleUnitId,
          this.firstExecutionDate,
          this.existingWorkGroupLeadTime,
        );

        this.createObjectGroupAndAssignObjectWithProtocol();
      } else if (this.selectedMode == WorkGroupsConfigMode.createNew) {
        this.createObjectGroupAndAssignObjectWithProtocol();
      } else if (this.selectedMode === WorkGroupsConfigMode.evaluate) {
        const objectsTotalCount = this.groupSelectionComponent.objectSelection.objectSelectionGrid.instance
          .getDataSource()
          ?.items().length;

        let selectedWorkTemplates = this.cycleWorkTypeIds;
        let selectedAnlagenIds = this.objectGroupService.evaluatedAnlagen.map((item: any) => item.Id);

        if (this.cycleWorkTypeIds.length === this.cycleWorkTypesTotalCount) {
          selectedWorkTemplates = [];
        }
        if (this.assetsCount === objectsTotalCount) {
          selectedAnlagenIds = [];
        }

        this.objectGroupService.evaluateObjWorkTypesWithProtocol(
          selectedWorkTemplates,
          selectedAnlagenIds,
          this.arbeitStatusNumber,
        );

        this.displayBusybar = false;
      }
    }
  }

  private createObjectGroupAndAssignObjectWithProtocol(): void {
    let options: ProcessGroupOptions;
    if (this.selectedMode === WorkGroupsConfigMode.createNew) {
      options = {
        mode: this.selectedMode,
        selectedAssetIds: this.groupSelectionComponent.getSelectedKeys(this.objSelectionGroupName),
        addedObjectsRawData: this.groupSelectionComponent.getSelectedDataRows(this.objSelectionGroupName),
        designation: this.designation,
        isMaintenanceCycleChecked: this.isMaintenanceCycleChecked,
        cycleLength: this.cycleLength,
        selectedCycleUnitId: this.selectedCycleUnitId,
        selectedGroupTypeId: this.selectedGroupTypeId,
        firstExecutionDate: this.isMaintenanceCycleChecked ? this.firstExecutionDate : null,
        leadTime: this.isMaintenanceCycleChecked ? this.leadTime : null,
        selectedAnlageGroupId: this.selectedAnlageGroupId,
        alreadyAssignedIds: this.alreadyAssignedIds,
        originalAnlageDetailMap: this.originalAnlageDetailMap,
        groupInfos: this.workGroupData.find((g) => g.Id === this.selectedAnlageGroupId),
      };
    } else {
      // EDIT EXISTING MODE
      const groupInfo = this.workGroupData.find((g) => g.Id === this.selectedAnlageGroupId);

      options = {
        mode: this.selectedMode,
        selectedAssetIds: this.groupSelectionComponent.getSelectedKeys(this.objSelectionGroupName),
        addedObjectsRawData: this.groupSelectionComponent.getSelectedDataRows(this.objSelectionGroupName),
        designation: this.designation,
        isMaintenanceCycleChecked: this.isExistingWorkGroupMaintenanceCycleChecked,
        cycleLength: this.existingWorkGroupCycleLength,
        selectedCycleUnitId: this.existingWorkGroupCycleUnitId,
        selectedGroupTypeId: groupInfo?.WorkTypeId || null, // Use the group's existing WorkTypeId
        firstExecutionDate: this.firstExecutionDate,
        leadTime: this.existingWorkGroupLeadTime,
        selectedAnlageGroupId: this.selectedAnlageGroupId,
        alreadyAssignedIds: this.alreadyAssignedIds,
        originalAnlageDetailMap: this.originalAnlageDetailMap,
        groupInfos: groupInfo,
      };
    }

    this.objectGroupService.processGroupConfiguration(options);
  }

  private getCycleUnitDescription(cycleUnitId: number | null): string {
    if (cycleUnitId === null) return '';
    const unit = this.cycleUnitList.find((u) => u.Id === cycleUnitId);
    return unit?.Description || '';
  }

  private getWorkTypeDescription(workTypeId: string | undefined): string {
    if (!workTypeId) return '';
    const workType = this.groupTemplateData?.find((wt: any) => wt.Id === workTypeId);
    return workType?.Description || '';
  }

  private getWorkTypeObjectTypeDefinition(workTypeId: string | undefined): string {
    if (!workTypeId) return '';
    const workType = this.groupTemplateData?.find((wt: any) => wt.Id === workTypeId);
    return workType?.objectTypeDefinition || '';
  }

  private generateProtocol(protocolData: ObjectGroupGenerationResult): void {
    const protocolPopupTitle = this.res.get('ObjectGroupConfigurationProtocolTitle');
    const categoryDescriptionInfos = new Map<string, string>();
    const customColumnInfos = new Map<string, any[]>();
    const result: any = {};

    // --- Category: Created Groups ---
    if (protocolData.createdGroups.length > 0) {
      const categoryName = this.res.get('CreatedGroups');
      categoryDescriptionInfos.set(categoryName, this.res.get('CreatedGroupsDescription'));

      const formattedData = protocolData.createdGroups.map((group) => ({
        ...group,
        CycleUnit: this.getCycleUnitDescription(group.CycleUnitId),
        WorkType: this.getWorkTypeDescription(group.WorkTypeId),
      }));

      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'WorkType', caption: this.res.get('GroupTemplate') },
        { dataField: 'CycleLength', caption: this.res.get('Interval') },
        { dataField: 'CycleUnit', caption: this.res.get('CycleUnit') },
        {
          dataField: 'FirstExecutionDate',
          caption: this.res.get('FirstExecution'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        { dataField: 'LeadTime', caption: this.res.get('PlanningLeadTime') },
      ]);
    }

    // --- Category: Edited Groups ---
    if (protocolData.editedGroups.length > 0) {
      const categoryName = this.res.get('EditedAssetGroups');
      categoryDescriptionInfos.set(categoryName, this.res.get('EditedGroupsDescription'));

      const formattedData = protocolData.editedGroups.map((group) => ({
        ...group,
        Zykluseinheit: this.getCycleUnitDescription(group.CycleUnitId ?? null),
        Arbeitsvorlage: this.getWorkTypeDescription(group.WorkTypeId),
      }));

      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'Arbeitsvorlage', caption: this.res.get('GroupTemplate') },
        { dataField: 'Intervall', caption: this.res.get('Interval') },
        { dataField: 'Zykluseinheit', caption: this.res.get('CycleUnit') },
        {
          dataField: 'ErsteAusfuehrung',
          caption: this.res.get('FirstExecution'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        { dataField: 'LeadTime', caption: this.res.get('PlanningLeadTime') },
      ]);
    }

    // --- Category: Added Objects ---
    if (protocolData.addedObjects.length > 0) {
      const categoryName =
        this.selectedMode === WorkGroupsConfigMode.createNew
          ? this.res.get('ContainedAssets')
          : this.res.get('AddedAssets');
      const description =
        this.selectedMode === WorkGroupsConfigMode.createNew
          ? this.res.get('ContainedAssetsDescription')
          : this.res.get('AddedAssetsDescription');

      const formattedData = protocolData.addedObjects.map((row) => ({
        ...row,
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));

      result[categoryName] = formattedData;
      categoryDescriptionInfos.set(categoryName, description);
      customColumnInfos.set(categoryName, [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'ObjectState', caption: this.res.get('Status') },
        { dataField: 'District', caption: this.res.get('District') },
        { dataField: 'Street', caption: this.res.get('Street') },
        { dataField: 'Subject', caption: this.res.get('WorkArea') },
      ]);
    }

    // --- Category: Removed Objects ---
    if (protocolData.removedObjects.length > 0) {
      const categoryName = this.res.get('RemovedObjects');
      categoryDescriptionInfos.set(categoryName, this.res.get('RemovedObjectsDescription'));

      const formattedData = protocolData.removedObjects.map((row) => ({
        ...row,
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));

      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'ObjectState', caption: this.res.get('Status') },
        { dataField: 'District', caption: this.res.get('District') },
        { dataField: 'Street', caption: this.res.get('Street') },
        { dataField: 'Subject', caption: this.res.get('WorkArea') },
      ]);
    }

    // --- Category: Created Maintenance Cycles ---
    if (protocolData.createdMaintenanceCycles.length > 0) {
      const categoryName = this.res.get('CreatedMaintenanceCycles');
      categoryDescriptionInfos.set(categoryName, this.res.get('CreatedMaintenanceCyclesDescription'));

      const formattedData = protocolData.createdMaintenanceCycles.map((cycle) => ({
        ...cycle,
        Zykluseinheit: this.getCycleUnitDescription(cycle.CycleUnitId),
        Arbeitsvorlage: this.getWorkTypeDescription(cycle.WorkTypeId),
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          cycle,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));

      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Bezeichnung', caption: this.res.get('Designation') },
        { dataField: 'Anlage', caption: this.res.get('Asset') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'Intervall', caption: this.res.get('Interval') },
        { dataField: 'Zykluseinheit', caption: this.res.get('CycleUnit') },
        { dataField: 'Arbeitsvorlage', caption: this.res.get('GroupTemplate') },
        {
          dataField: 'ErsteAusfuehrung',
          caption: this.res.get('FirstExecution'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
      ]);
    }

    // --- Category: Failed Maintenance Cycles ---
    if (protocolData.failedMaintenanceCycles.length > 0) {
      const categoryName = this.res.get('FailedMaintenanceCycles');
      categoryDescriptionInfos.set(categoryName, this.res.get('FailedMaintenanceCyclesDescription'));

      const formattedData = protocolData.failedMaintenanceCycles.map((row: any) => ({
        ...row,
        Zykluseinheit: this.getCycleUnitDescription(row.CycleUnitId),
        Arbeitsvorlage: this.getWorkTypeDescription(row.WorkTypeId),
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));

      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Bezeichnung', caption: this.res.get('Designation') },
        { dataField: 'Anlage', caption: this.res.get('Asset') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'Intervall', caption: this.res.get('Interval') },
        { dataField: 'Zykluseinheit', caption: this.res.get('CycleUnit') },
        { dataField: 'Arbeitsvorlage', caption: this.res.get('GroupTemplate') },
        {
          dataField: 'ErsteAusfuehrung',
          caption: this.res.get('FirstExecution'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
      ]);
    }

    // --- Show the final protocol ---
    if (Object.keys(result).length > 0) {
      this.protocolViewerService.openProtocolViewer(
        result,
        customColumnInfos,
        categoryDescriptionInfos,
        protocolPopupTitle,
        undefined,
        undefined,
        true,
      );
    }
  }

  private generateEvaluationProtocol(protocolData: MaintenanceCycleEvaluationResult): void {
    const protocolPopupTitle = this.res.get('ObjectGroupConfigurationProtocolTitle');
    const categoryDescriptionInfos = new Map<string, string>();
    const customColumnInfos = new Map<string, any[]>();
    const result: any = {};

    //----- Category : Evaluated Work Templates -----
    if (protocolData.evaluatedWorkTemplates.length > 0) {
      const categoryName = this.res.get('EvaluatedWorkTemplates');
      categoryDescriptionInfos.set(categoryName, this.res.get('EvaluatedWorkTemplatesDescription'));
      result[categoryName] = this.objectGroupService.evaluatedWorkTemplates;
      customColumnInfos.set(categoryName, [{ dataField: 'Designation', caption: this.res.get('Designation') }]);
    }

    //---- Category : Evaluated Anlagen -----
    if (protocolData.evaluatedAnlagen.length > 0) {
      const categoryName = this.res.get('EvaluatedAssets');
      categoryDescriptionInfos.set(categoryName, this.res.get('EvaluatedAssetsDescription'));
      const formattedData = this.objectGroupService.evaluatedAnlagen.map((row: any) => ({
        ...row,
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));
      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'Status', caption: this.res.get('Status') },
        { dataField: 'Ref_Ortsteil', caption: this.res.get('District') },
        { dataField: 'Ref_Strasse', caption: this.res.get('Street') },
        { dataField: 'Ref_Arbeitsbereich', caption: this.res.get('WorkArea') },
      ]);
    }

    //---- Category : Generated Works -----
    if (protocolData.generatedWorks) {
      const categoryName = this.res.get('GeneratedWorks');
      categoryDescriptionInfos.set(categoryName, this.res.get('GeneratedWorksDescription'));
      const formattedData = protocolData.generatedWorks.map((row: any) => ({
        ...row,
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));
      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'WorkType', caption: this.res.get('Designation') },
        { dataField: 'Object', caption: this.res.get('Asset') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'WorkStateText', caption: this.res.get('Status') },
        {
          dataField: 'EarliestExecutionDate',
          caption: this.res.get('ExecutionFrom'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        {
          dataField: 'ScheduledExecutionDate',
          caption: this.res.get('ExecutionPlanned'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        {
          dataField: 'LatestExecutionDate',
          caption: this.res.get('ExecutionTill'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        { dataField: 'CycleLength', caption: this.res.get('Interval') },
        { dataField: 'CycleUnitText', caption: 'Zykleinheit' },
      ]);
    }

    //---- Category : Anlagen with existing Works ----
    if (protocolData.anlagenWithExistingWorks.length > 0) {
      const categoryName = this.res.get('AssetsWithExistingWork');
      categoryDescriptionInfos.set(categoryName, this.res.get('AssetsWithExistingWorkDescription'));
      const formattedData = protocolData.anlagenWithExistingWorks.map((row: any) => ({
        ...row,
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));
      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Object', caption: this.res.get('Designation') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'ObjectState', caption: this.res.get('Status') },
        { dataField: 'District', caption: this.res.get('District') },
        { dataField: 'Street', caption: this.res.get('Street') },
        { dataField: 'Subject', caption: this.res.get('WorkArea') },
        { dataField: 'WorkType', caption: this.res.get('GroupTemplate') },
      ]);
    }

    //---- Category : Anlagen  without due Maintenance Cycle -----
    if (protocolData.anlagenWithoutDueMaintenanceCycle.length > 0) {
      const categoryName = this.res.get('AssetsWithoutDueMaintenanceCycle');
      categoryDescriptionInfos.set(categoryName, this.res.get('AssetsWithoutDueMaintenanceCycleDescription'));
      const formattedData = protocolData.anlagenWithoutDueMaintenanceCycle.map((row: any) => ({
        ...row,
        MasterObjectTypeDesignation: this.calcObjectTypeDesignation(
          row,
          this.objectTypeDesignations,
          this.lookupNewObjectTypeTrigger$,
        ),
      }));
      result[categoryName] = formattedData;
      customColumnInfos.set(categoryName, [
        { dataField: 'Object', caption: this.res.get('Designation') },
        { dataField: 'MasterObjectTypeDesignation', caption: this.res.get('AssetType') },
        { dataField: 'ObjectState', caption: this.res.get('Status') },
        { dataField: 'District', caption: this.res.get('District') },
        { dataField: 'Street', caption: this.res.get('Street') },
        { dataField: 'Subject', caption: this.res.get('WorkArea') },
        { dataField: 'WorkType', caption: this.res.get('GroupTemplate') },
      ]);
    }

    // --- Show the final protocol ---
    if (Object.keys(result).length > 0) {
      this.protocolViewerService.openProtocolViewer(
        result,
        customColumnInfos,
        categoryDescriptionInfos,
        protocolPopupTitle,
        undefined,
        undefined,
        true,
        true,
      );
    }
  }

  private validateSourceDataStep(): string {
    let msg: string = '';

    if (!this.designation) {
      msg += `<div>${this.res.get('WorkGroupDesignationValidationText')}</div>`;
    }

    if (!this.selectedGroupTypeId) {
      msg += `<div>${this.res.get('WorkTemplateValidationText')}</div>`;
    }

    if (!this.cycleLength) {
      msg += `<div>${this.res.get('CycleLengthValidationText')}</div>`;
    }

    return msg;
  }

  private validateEditExistingSourceDataStep(): string {
    let msg: string = '';

    if (this.isExistingWorkGroupMaintenanceCycleChecked) {
      if (!this.existingWorkGroupCycleLength) {
        msg += `<div>${this.res.get('CycleLengthValidationText')}</div>`;
      }

      if (this.existingWorkGroupCycleUnitId === null) {
        msg += `<div>${this.res.get('CycleUnitValidationText')}</div>`;
      }

      if (!this.firstExecutionDate) {
        msg += `<div>${this.res.get('FirstExecutionDateValidationText')}</div>`;
      }
    }

    return msg;
  }

  private validateWorkTemplateAndStatusSelection(): string {
    let msg: string = '';

    const selectedIds = this.worktemplateSelectionComponent.getSelectedKeys(this.workTemplateSelectionGroupName);

    if (!Array.isArray(selectedIds) || !selectedIds.length) {
      msg += `<div>${this.res.get('WorkTemplateSelectionValidationText')}</div>`;
    }

    if (!this.arbeitStatusText) {
      msg += `<div>${this.res.get('WorkStatusSelectionValidationText')}</div>`;
    }

    return msg;
  }

  private onStepValidation(step: MatStep, msg: string): void {
    step.completed = msg === null || msg.trim().length === 0;

    if (step.completed) {
      this.stepper.next();
    } else {
      this.workService.showErrorInfo(msg);
    }
  }

  private createObjectSelectionGroups(): Observable<ObjectSelectionGroup[]> {
    let additionalQueryColumns: QueryColumn[] = [];
    let additionalGridColumns: dxDataGridColumn[] = [];
    let additionalQueryParameters: QueryParameter[] = [];
    const dataQueryOPathes = new DataQueryOPathes();

    additionalQueryColumns = [
      { Name: 'MtObjWorkTypeId', OPath: 'null' },
      { Name: 'WorkExecTimeInDays', OPath: `$${dataQueryOPathes.manualMode.paramName_WorkExecTimeInDays}` },
      {
        Name: 'HasNoExistingWorksFilter',
        OPath: dataQueryOPathes.manualMode.mtObjectHasNoExistingWorksFilterOPath,
        IsFilter: true,
      },
      {
        Name: 'WW_Kanalart',
        OPath:
          'CaseTrue(IsTypeValue(GeObjectType,AbwLeitung), Type(AbwLeitung)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].Kanalart, IsTypeValue(GeObjectType,AbwKnoten), Type(AbwKnoten)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].Kanalart)',
      },
      {
        Name: 'WW_Anschlussfunktion',
        OPath:
          'CaseNull(Type(AbwLeitung)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].GetValue(Anschlussfunktion), Type(AbwKnoten)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].GetValue(Anschlussfunktion))',
      },
      {
        Name: 'Fachbereich',
        OPath: `CaseWhen(Kind, 0, 'Abwasser', 1, 'Wasser', 2, 'Gas', 3, 'Kabel', 4, 'GeoObjekt', 5, 'Straße', ref:Kind)`,
      },
      { Name: 'StrVerkehrsflaeche_Art', OPath: 'ref:Type(StrFlaeche)[Id = ^.IdMasterObject].Art' },
      { Name: 'StrVerkehrsflaeche_Belagsart', OPath: 'ref:Type(StrFlaeche)[Id = ^.IdMasterObject].Belagsart' },
      {
        Name: 'StrVerkehrsflaeche_Strasse',
        OPath: 'Type(StrFlaeche)[Id = ^.IdMasterObject].Abschnitt.Strasse.Name',
      },
    ];

    if (this.isEvaluationMode()) {
      additionalQueryColumns.push({
        Name: 'ObjWorkTypeNames',
        OPath: this.getObjWorkTypeNamesExpression(),
        IsHidden: !this.isEvaluationMode(),
      });
    }

    additionalGridColumns = [
      { dataField: 'MtObjWorkTypeId', visible: false, allowFiltering: false, showInColumnChooser: false },
      { dataField: 'WorkExecTimeInDays', visible: false, allowFiltering: false, showInColumnChooser: false },
      { dataField: 'WW_Kanalart', caption: this.res.get('ChannelArt'), visible: false, showInColumnChooser: true },
      {
        dataField: 'WW_Anschlussfunktion',
        caption: this.res.get('ConnectionFunction'),
        visible: false,
        showInColumnChooser: true,
      },
      { dataField: 'Fachbereich', caption: this.res.get('Department'), visible: false, showInColumnChooser: true },
      {
        dataField: 'StrVerkehrsflaeche_Art',
        caption: this.res.get('TrafficAreaArt'),
        visible: false,
        showInColumnChooser: true,
      },
      {
        dataField: 'StrVerkehrsflaeche_Belagsart',
        caption: this.res.get('TrafficAreaPavementType'),
        visible: false,
        showInColumnChooser: true,
      },
      {
        dataField: 'StrVerkehrsflaeche_Strasse',
        caption: this.res.get('TrafficAreaStreet'),
        visible: false,
        showInColumnChooser: true,
      },
    ];

    additionalQueryParameters = [];
    const oPathFilter = this.getMtObjectFilterByMode();

    const workGroupToGenerateGroup: ObjectSelectionGroup = {
      name: this.objSelectionGroupName,
      designation: 'Anlagen',
      layoutStorage: this.gridLayoutStorage,
      layoutStorageKey: this.getLayoutStorageKeyByMode(),
      preselectedKeys: this.selectedMode == WorkGroupsConfigMode.editExisting ? this.preselectedKeys : [],
      objectTypeNameOrQueryInfo: {
        objectSpace: 'BaSYS.Complete',
        objectType: 'MtObject',
        oPathFilter: oPathFilter,
        columns: [
          { Name: 'Id', OPath: 'Id' },
          { Name: 'ObjectType', OPath: 'ObjectType' },
          { Name: 'Designation', OPath: 'Designation', SortOrder: QueryColumnSortOrder.Ascending },
          { Name: 'MasterObjectType', OPath: 'GeObjectType' },
          { Name: 'MasterObjectId', OPath: 'GeObjectId' },
          { Name: 'Ref_Strasse', OPath: 'ref:IdRefStrasse' },
          { Name: 'Status', OPath: 'ref:Status' },
          { Name: 'Ref_Ortsteil', OPath: 'ref:IdRefOrtsteil' },
          { Name: 'Ref_Arbeitsbereich', OPath: 'ref:IdRefArbeitsbereich' },
          { Name: 'MtWorkTypeId', OPath: '$WorkTypeId' },
          { Name: 'Designation', OPath: 'Designation', SortOrder: QueryColumnSortOrder.Ascending },
          ...additionalQueryColumns,
        ],
        columnHeaders: this.getColumnHeaders(additionalGridColumns),
      },
    };

    return of([workGroupToGenerateGroup]);
  }

  private getLayoutStorageKeyByMode(): string | undefined {
    const prefix = 'config-objectgroup.';
    if (this.selectedMode === WorkGroupsConfigMode.evaluate)
      return prefix + this.objSelectionGroupNameWithWorkTempalteColumn;
    else {
      return prefix + this.objSelectionGroupName;
    }
  }

  private getColumnHeaders(additionalGridColumns: dxDataGridColumn<any, any>[]): any {
    const ret = [
      { dataField: 'Id', dataType: 'string', visible: false, allowFiltering: true, showInColumnChooser: false },
      {
        dataField: 'ObjectType',
        dataType: 'number',
        visible: false,
        allowFiltering: false,
        showInColumnChooser: false,
      },
      { dataField: 'Designation', caption: this.res.get('Designation') },
      {
        dataField: 'MasterObjectType',
        dataType: 'number',
        visible: false,
        allowFiltering: false,
        showInColumnChooser: false,
      },
      {
        dataField: 'MasterObjectId',
        dataType: 'string',
        visible: false,
        allowFiltering: false,
        showInColumnChooser: false,
      },
      {
        dataField: 'MasterObjectTypeDesignation',
        caption: this.res.get('AssetType'),
        calculateCellValue: (rowData: any) =>
          this.calcObjectTypeDesignation(rowData, this.objectTypeDesignations, this.lookupNewObjectTypeTrigger$),
        allowSorting: true,
        allowFiltering: true,
        allowSearch: true,
        allowGrouping: true,
      },
      { dataField: 'Ref_Strasse', caption: this.res.get('Street') },
      { dataField: 'Ref_Ortsteil', caption: this.res.get('District') },
      { dataField: 'Status', caption: this.res.get('Status') },
      { dataField: 'Ref_Arbeitsbereich', caption: this.res.get('WorkArea'), visible: false },
      { dataField: 'MtWorkTypeId', visible: false, allowFiltering: false, showInColumnChooser: false },
      ...additionalGridColumns,
    ];

    if (this.isEvaluationMode()) {
      const gridColumnCycleWorkTypeNames = {
        dataField: 'ObjWorkTypeNames',
        caption: this.res.get('MtObjectWorkTypesDisplay'),
        visible: this.isEvaluationMode(),
        showInColumnChooser: false,
      };
      ret.splice(6, 0, gridColumnCycleWorkTypeNames);
    }

    return ret;
  }

  private isEvaluationMode(): boolean {
    return this.selectedMode === WorkGroupsConfigMode.evaluate;
  }

  private getObjWorkTypeNamesExpression(): string {
    if (this.cycleWorkTypeIds && this.cycleWorkTypeIds.length > 0) {
      const ids: string = this.toOPathInList(this.cycleWorkTypeIds);
      return `WorkTypes[WorkTypeId In ${ids}].WorkType.JoinPath(Designation, ', ')`;
    } else return `WorkTypes.WorkType.JoinPath(Designation, ', ')`;
  }

  private async createWorkTemplateSelectionGroups(): Promise<ObjectSelectionGroup[]> {
    const workTemplateSelectionGroup: ObjectSelectionGroup = {
      name: this.workTemplateSelectionGroupName,
      designation: 'Arbeitsvorlagen',
      layoutStorage: this.gridLayoutStorage,
      layoutStorageKey: 'config-objectgroup.' + this.workTemplateSelectionGroupName,
      objectTypeNameOrQueryInfo: {
        objectSpace: 'BaSYS.Complete',
        objectType: 'MtWorkType',
        oPathFilter: await AppService.getWorkTypeFilterCore(this.userService),

        columns: [
          { Name: 'Id', OPath: 'Id' },
          { Name: 'Designation', OPath: 'Designation', SortOrder: QueryColumnSortOrder.Ascending },
        ],
        columnHeaders: [
          { dataField: 'Id', dataType: 'string', visible: false, allowFiltering: true, showInColumnChooser: false },
          { dataField: 'Designation', caption: this.res.get('Designation') },
        ],
      },
    };

    return [workTemplateSelectionGroup];
  }

  private getMtObjectFilterByMode(): string {
    let retFilter = 'ParentId=$CurrentMunicipalityVariantId';
    if (this.selectedMode === WorkGroupsConfigMode.evaluate) {
      if (!!this.cycleWorkTypeIds && this.cycleWorkTypeIds.length > 0) {
        const ids: string = this.toOPathInList(this.cycleWorkTypeIds);
        if (ids) {
          retFilter += ' And Exists(WorkTypes[WorkTypeId In ' + ids + '])';
        }
      }
    } else if (this.selectedMode === WorkGroupsConfigMode.createNew) {
      const objectTypeDefinition = this.getWorkTypeObjectTypeDefinition(this.selectedGroupTypeId);
      if (objectTypeDefinition) {
        retFilter =
          `(Id In (${objectTypeDefinition}.Id) Or IdMasterObject In (${objectTypeDefinition}.Id))` +
          ' And ' +
          retFilter;
      }
    }

    return retFilter;
  }

  private toOPathInList(ids: string[]): string {
    if (!ids || ids.length === 0) {
      return "('')";
    }

    const quoted = ids.map((id) => `'${id}'`).join(',');
    return `(${quoted})`;
  }

  private showMessageAsHtml(htmlMsg: string, messageMode: MessageMode = MessageMode.Info): void {
    const settings = new MessageSettings();
    settings.mode = messageMode;
    let icon = MessageBoxIcon.Info;
    switch (messageMode) {
      case MessageMode.Error:
        icon = MessageBoxIcon.Error;
        break;
      case MessageMode.Warning:
        icon = MessageBoxIcon.Warning;
        break;
      case MessageMode.Question:
        icon = MessageBoxIcon.Question;
        break;
    }

    settings.icon = icon;
    settings.button = MessageBoxButtons.OK;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;
    if (this.alive) {
      this.messageBoxService
        .showMessageBox(settings)
        .pipe(takeWhile(() => this.alive))
        .subscribe();
    }
  }

  private calcObjectTypeDesignation(
    rowData: any,
    objectTypeDesignations: Map<number, string>,
    lookupNewObjectTypeTrigger$: BehaviorSubject<number>,
  ): string {
    const otNumber = Number(rowData.MasterObjectType);
    let otDesignation = objectTypeDesignations.get(otNumber);

    if (!otDesignation) {
      lookupNewObjectTypeTrigger$.next(otNumber);
      otDesignation = String(otNumber);
    }

    return otDesignation;
  }

  private areArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((id) => b.includes(id));
  }

  private onWorkTemplateSelectionChange(): boolean {
    if (this.selectedMode === WorkGroupsConfigMode.evaluate) {
      const newSelectedIds = this.worktemplateSelectionComponent.getSelectedKeys(this.workTemplateSelectionGroupName);
      this.cycleWorkTypeIds = newSelectedIds;

      this.objectGroupService.evaluatedWorkTemplates = this.worktemplateSelectionComponent.getSelectedDataRows(
        this.workTemplateSelectionGroupName,
      );

      const hasChanged = !this.areArraysEqual(this.previousCycleWorkTypeIds, newSelectedIds);

      if (hasChanged) {
        this.previousCycleWorkTypeIds = [...newSelectedIds];
      }

      return hasChanged;
    }

    return false;
  }

  public stepSelectionChange(eventArgs: StepperSelectionEvent): void {
    if (eventArgs.selectedStep === this.stepObjectSelection) {
      if (!this.lastWorkGroupQueryInfo || this.lastWorkGroupQueryInfo.Mode !== this.selectedMode) {
        this.refreshObjectSelection();
        this.assetsCount = 0;
      }
    }

    if (this.selectedMode === WorkGroupsConfigMode.evaluate) {
      if (this.onWorkTemplateSelectionChange()) {
        this.refreshObjectSelection();
      }

      this.objectGroupService.evaluatedAnlagen = this.groupSelectionComponent.getSelectedDataRows(
        this.objSelectionGroupName,
      );
    }

    if (eventArgs.selectedStep === this.stepWelcome) {
      this.welcomeStepLabel = this.res.get('WizardWelcome');
    } else {
      this.welcomeStepLabel =
        this.res.get('WizardWelcome') +
        ' (' +
        (this.selectedMode === WorkGroupsConfigMode.createNew
          ? this.res.get('CreateNewWorkGroup') + ')'
          : this.selectedMode === WorkGroupsConfigMode.editExisting
            ? this.res.get('EditExistingWorkGroup') + ')'
            : this.res.get('EvaluateMaintenanceCycles') + ')');
    }
  }

  private refreshObjectSelection(): void {
    this.refreshGroupsToken$.next(true);
  }

  private refreshWorkTemplateSelection(): void {
    this.refreshWorkTemplateGroupsToken$.next(true);
  }

  public countAssets(assetsCount: number): void {
    this.assetsCount = assetsCount;
  }
}
